/**
 * @file OrderOverviewTab.tsx
 * @description Вкладка «Обзор» заказа (phase10; phase19.14 — блок «Сборка»;
 * phase19.31 — без повтора шапки).
 *
 * Клиент, источник, сумма, оплата, доставка, ответственный и дата изменения
 * уже стоят в сведениях под шапкой — здесь только то, чего там нет: деньги
 * подробно (НДС, скидка), связь со сделкой и организацией, даты этапов,
 * что меняли последним и заметки. Подписи по-русски, обычным регистром,
 * деньги — «1 175,00 ₽» (SC43).
 *
 * На стадии «На сборке» сверху — блок «Сборка» (экран C шага 0 phase19.14):
 * сам грузит данные и прячется, если задания нет или у роли нет прав склада.
 *
 * phase19.16, партия «Юридические документы» (шаг Б, uiux-legal-steps-b-c
 * §2): второй карточкой — «Принятые условия» (`OrderLegalTermsCard`).
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import { OrderLegalTermsCard } from "./OrderLegalTermsCard";
import { OrderPickingSection } from "./OrderPickingSection";
import { useHasPermission } from "../../auth/hooks";
import { EditorialCard } from "../ui/EditorialCard";
import { SectionLabel } from "../ui/SectionLabel";
import { currencySymbol, formatMoney, type OrderDto } from "../../lib/api-orders";
import { isLegalEntityOrder } from "../../lib/order-summary-lines";
import { formatShopDateTime } from "../../lib/shop-time";

interface Props {
  order: OrderDto;
  /** Slug магазина — ссылки «Открыть задание» и «Открыть сделку». */
  shopSlug?: string;
}

/** Даты этапов заказа: подпись и поле — данными, без ветки на каждую дату. */
const STAGE_DATES: { label: string; field: keyof Pick<OrderDto, "confirmedAt" | "paidAt" | "shippedAt" | "deliveredAt" | "completedAt"> }[] = [
  { label: "Подтверждён", field: "confirmedAt" },
  { label: "Оплачен", field: "paidAt" },
  { label: "Отгружен", field: "shippedAt" },
  { label: "Доставлен", field: "deliveredAt" },
  { label: "Завершён", field: "completedAt" },
];

export function OrderOverviewTab({ order, shopSlug }: Props) {
  const rub = currencySymbol(order.currency);
  const discount = Number(order.discountAmount) > 0;
  // Ссылка на сделку — только тем, кому открыта CRM: иначе переход в «нет доступа».
  const canOpenDeal = useHasPermission("SHOP_USE_CRM");

  return (
    <div className="flex flex-col gap-4">
      {order.stage?.kind === "PACKAGING" ? <OrderPickingSection orderId={order.id} shopSlug={shopSlug} /> : null}
      <EditorialCard>
        <EditorialCard.Body className="flex flex-col gap-4">
          <dl className="type-body grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="В том числе НДС" value={`${formatMoney(order.totalVat)} ${rub}`} />
            {discount ? <Field label="Скидка" value={`${formatMoney(order.discountAmount)} ${rub}`} /> : null}
            {isLegalEntityOrder(order) ? <Field label="Покупатель" value="Организация" /> : null}
            {order.dealId ? (
              <Field
                label="Сделка"
                value={
                  shopSlug && canOpenDeal ? (
                    <Link to={`/shop/${shopSlug}/crm/deals/${order.dealId}`} className="text-link underline underline-offset-2">
                      Открыть сделку
                    </Link>
                  ) : (
                    "Заказ создан из сделки"
                  )
                }
              />
            ) : null}
            {STAGE_DATES.map(({ label, field }) => {
              const at = order[field];
              return at ? <Field key={field} label={label} value={formatShopDateTime(at)} /> : null;
            })}
            {order.cancelledAt ? (
              <Field
                label="Отменён"
                value={`${formatShopDateTime(order.cancelledAt)}${order.cancelReason ? ` · ${order.cancelReason}` : ""}`}
              />
            ) : null}
            {/* Что меняли последним — текст последней значимой записи ленты (phase19.15). */}
            {order.lastChangeSummary ? <Field label="Последнее изменение" value={order.lastChangeSummary} /> : null}
          </dl>
          {order.notes ? (
            <div className="flex flex-col gap-1">
              <SectionLabel as="h2">Заметки</SectionLabel>
              <p className="type-body whitespace-pre-wrap">{order.notes}</p>
            </div>
          ) : null}
        </EditorialCard.Body>
      </EditorialCard>
      <OrderLegalTermsCard order={order} shopSlug={shopSlug} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="type-caption text-on-surface-variant">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}
